/**
 * Главный компонент для управления конфигурациями
 */

import React, { FC, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Form, Select, Button, Card, Space, message } from 'antd';
import { SaveOutlined, ReloadOutlined, PlusOutlined } from '@ant-design/icons';
import { Redirect } from 'react-router-dom';
import { Dispatch, iRootState } from '../../../store';
import { useAsync, useRequestHandler } from '../../../hooks';
import { guids } from '../../Root/paths';
import isSatisfied from '../../../utils/isSatisfied';

import Spinner from '../../../components_/Spinner';
import EmptyState from '../../../components_/EmptyState';

import DynamicForm from './DynamicForm';
import FieldEditor from './FieldEditor';
import { flattenObjectToFormValues, formValuesToNestedObject, validateJSON } from './utils';
import { parseJSON5WithComments, exportToJSON5WithComments } from './json5Parser';

const { Option } = Select;

const CONFIG_TYPES = [
  { value: 'ble', label: 'BLE' },
  { value: 'company', label: 'Company' },
  { value: 'timetable', label: 'Timetable' }
];

const mapState = (state: iRootState) => ({
  data: state.settingConfig.data,
  configType: state.settingConfig.configType,
  loading: state.settingConfig.loading,
  hasRights: isSatisfied({ uid: guids.settings.config.root, action: 'Visibility' }, state.securable.data)
});

const mapDispatch = (dispatch: any) => {
  const d = dispatch as Dispatch;

  return {
    getConfig: d.settingConfig.getConfig,
    saveConfig: d.settingConfig.saveConfig,
    reset: d.settingConfig.reset
  };
};

type connectedProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;

const Config: FC<connectedProps> = ({
  data,
  configType,
  loading,
  hasRights,
  getConfig,
  saveConfig,
  reset
}) => {
  // Если нет прав на просмотр конфигурации, редирект на главную настроек
  if (!hasRights) {
    return <Redirect to="/settings" />;
  }
  const [form] = Form.useForm();
  const [localData, setLocalData] = useState<any>(null);
  const [comments, setComments] = useState<{ [key: string]: string }>({});
  const [activeParentPath, setActiveParentPath] = useState<string[]>([]);
  const [showFieldEditor, setShowFieldEditor] = useState(false);
  const requestHandler = useRequestHandler();

  const [runGetConfig, isLoadingGetConfig] = useAsync(getConfig);
  const [runSaveConfig, isLoadingSaveConfig] = useAsync(saveConfig);

  useEffect(() => {
    // Загружаем конфигурацию при монтировании
    runGetConfig(configType);

    return () => {
      reset();
    };
  }, []);

  useEffect(() => {
    // Обновляем локальные данные и форму при изменении данных из Redux
    if (data) {
      // Если данные содержат комментарии, парсим их
      if (typeof data === 'string') {
        const { data: parsedData, comments: parsedComments } = parseJSON5WithComments(data);
        setLocalData(parsedData);
        setComments(parsedComments);
        form.setFieldsValue(flattenObjectToFormValues(parsedData));
      } else {
        setLocalData(data);
        setComments({});
        form.setFieldsValue(flattenObjectToFormValues(data));
      }
    }
  }, [data, form]);

  const handleConfigTypeChange = (newConfigType: string) => {
    runGetConfig(newConfigType);
  };

  const handleReload = () => {
    runGetConfig(configType);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const nestedObject = formValuesToNestedObject(values);

      // Валидация JSON
      const validation = validateJSON(nestedObject);
      if (!validation.valid) {
        message.error(`Ошибка валидации: ${validation.error}`);
        return;
      }

      // Экспортируем в JSON5 с комментариями
      const json5String = exportToJSON5WithComments(nestedObject, comments);

      const error = await runSaveConfig({ configType, data: json5String });

      requestHandler({
        error,
        entity: 'config',
        isEdit: true,
        onSuccess: () => {
          handleReload();
        }
      });
    } catch (error) {
      // Save error
      message.error('Ошибка при сохранении конфигурации');
    }
  };

  const handleAddField = (
    fieldName: string,
    fieldType: string,
    defaultValue: any,
    parentPath: string[] = []
  ) => {
    if (!localData) {
      return;
    }

    const updatedData = JSON.parse(JSON.stringify(localData)); // Deep clone

    // Находим родительский объект по пути
    let current = updatedData;
    for (const key of parentPath) {
      current = current[key];
    }

    // Добавляем новое поле
    current[fieldName] = defaultValue;

    setLocalData(updatedData);

    // Обновляем значение в форме
    const fieldPath = [...parentPath, fieldName].join('.');
    form.setFieldsValue({ [fieldPath]: defaultValue });

    setShowFieldEditor(false);
    setActiveParentPath([]);
  };

  const handleAddFieldToObject = (parentPath: string[]) => {
    setActiveParentPath(parentPath);
    setShowFieldEditor(true);
  };

  const handleOpenTopLevelFieldEditor = () => {
    setActiveParentPath([]);
    setShowFieldEditor(true);
  };

  const handleCloseFieldEditor = () => {
    setShowFieldEditor(false);
    setActiveParentPath([]);
  };

  const handleRemoveField = (fieldPath: string[]) => {
    if (!localData) {
      return;
    }

    const updatedData = { ...localData };
    let current = updatedData;

    // Находим родительский объект
    for (let i = 0; i < fieldPath.length - 1; i++) {
      current = current[fieldPath[i]];
    }

    // Удаляем поле
    delete current[fieldPath[fieldPath.length - 1]];

    setLocalData(updatedData);
    form.setFieldsValue(flattenObjectToFormValues(updatedData));
  };

  const handleCommentChange = (fieldPath: string, comment: string) => {
    setComments(prevComments => ({
      ...prevComments,
      [fieldPath]: comment
    }));
  };

  const isLoading = isLoadingGetConfig || isLoadingSaveConfig;

  if (isLoading) {
    return <Spinner />;
  }

  if (!data && !isLoading) {
    return <EmptyState variant="emptyRequest" />;
  }

  // Получаем существующие поля для текущего уровня (top-level или вложенный объект)
  const getExistingFields = () => {
    if (!localData) {
      return [];
    }

    let current = localData;
    for (const key of activeParentPath) {
      current = current[key];
    }

    return Object.keys(current);
  };

  const existingFields = getExistingFields();

  return (
    <div style={{ padding: '24px 0' }}>
      <Card>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {/* Заголовок с селектором типа конфигурации и кнопками */}
          <Space style={{ width: '100%', justifyContent: 'space-between' }}>
            <Space>
              <span>Тип конфигурации:</span>
              <Select
                value={configType}
                onChange={handleConfigTypeChange}
                style={{ width: 200 }}
                disabled={isLoading}
              >
                {CONFIG_TYPES.map(type => (
                  <Option key={type.value} value={type.value}>
                    {type.label}
                  </Option>
                ))}
              </Select>
            </Space>

            <Space>
              <Button icon={<ReloadOutlined />} onClick={handleReload} disabled={isLoading}>
                Обновить
              </Button>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleSave}
                disabled={isLoading}
              >
                Сохранить
              </Button>
            </Space>
          </Space>

          {/* Динамическая форма */}
          {localData && (
            <Form form={form} layout="vertical">
              <DynamicForm
                data={localData}
                comments={comments}
                onRemoveField={handleRemoveField}
                onAddFieldToObject={handleAddFieldToObject}
                onCommentChange={handleCommentChange}
              />

              {/* Кнопка добавления нового поля на верхнем уровне */}
              <Button
                type="dashed"
                onClick={handleOpenTopLevelFieldEditor}
                block
                icon={<PlusOutlined />}
                style={{ marginTop: 16 }}
              >
                Добавить поле
              </Button>
            </Form>
          )}

          {/* Модальное окно для добавления полей */}
          <FieldEditor
            onAddField={handleAddField}
            existingFields={existingFields}
            parentPath={activeParentPath}
            visible={showFieldEditor}
            onClose={handleCloseFieldEditor}
          />
        </Space>
      </Card>
    </div>
  );
};

const withConnect = connect(mapState, mapDispatch);

export default withConnect(Config);
